/**
 * Step 1: Jurisdiction Info
 * Displays jurisdiction chain and key rules (read-only)
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import type { WizardState } from '../../../types/lease-generation';
import type { DisclosureReadinessResponse } from '../../../lib/api';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { JurisdictionCoverageStatement } from '../../../components/JurisdictionCoverageStatement';

interface Step1Props {
  state: WizardState;
  loadJurisdiction: () => Promise<void>;
  unitId?: string;
  disclosureReadiness?: DisclosureReadinessResponse | null;
  missingRequiredMetadata?: number;
}

export function Step1JurisdictionInfo({ state, loadJurisdiction, unitId, disclosureReadiness, missingRequiredMetadata = 0 }: Step1Props) {
  const { jurisdictionInfo, isLoading, error } = state;

  useEffect(() => {
    if (!jurisdictionInfo) {
      loadJurisdiction();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-[3px] border-border border-t-primary rounded-full animate-spin" />
        <span className="ml-3 text-sm text-muted-foreground">Loading jurisdiction info...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-destructive/5 border border-destructive/20 rounded-lg">
        <p className="text-sm text-destructive">{error}</p>
        <Button
          variant="link"
          onClick={loadJurisdiction}
          className="mt-2 text-sm text-destructive p-0 h-auto"
        >
          Retry
        </Button>
      </div>
    );
  }

  if (!jurisdictionInfo) {
    return null;
  }

  const { rules } = jurisdictionInfo;

  return (
    <div className="flex flex-col gap-6">
      {/* Metadata Readiness Banner */}
      {missingRequiredMetadata > 0 && unitId && (
        <div
          className="flex flex-col gap-2 p-4 bg-warning/10 border border-warning/20 rounded-lg"
          data-testid="metadata-disclosureReadiness-banner"
          role="alert"
        >
          <p className="text-sm font-semibold text-warning m-0">
            Some unit details are incomplete.
          </p>
          <p className="text-sm text-warning m-0">
            Complete them on the unit page before generating a lease.
          </p>
          <Link
            to={`/units/${unitId}`}
            className="text-sm font-semibold text-warning underline"
            data-testid="metadata-disclosureReadiness-link"
          >
            Go to Unit Details →
          </Link>
        </div>
      )}

      {/* Disclosure Readiness Check */}
      {disclosureReadiness && !disclosureReadiness.ready && (
        <div
          className="flex flex-col gap-2 p-4 bg-warning/10 border border-warning/20 rounded-lg"
          data-testid="disclosure-disclosureReadiness-warning"
          role="alert"
        >
          <p className="text-sm font-semibold text-warning m-0">
            Required disclosures are incomplete
          </p>
          <p className="text-sm text-warning m-0">
            {disclosureReadiness.incompleteDisclosureIds.length > 0 && (
              <>
                The following disclosures must be completed before generating a lease:{' '}
                <strong>
                  {disclosureReadiness.incompleteDisclosureIds
                    .map((id) => id.replace(/_/g, ' '))
                    .join(', ')}
                </strong>
                .{' '}
              </>
            )}
          </p>
          {unitId && (
            <Link
              to={`/units/${unitId}/disclosures`}
              className="text-sm font-semibold text-warning underline"
              data-testid="nav-unit-disclosures"
            >
              Manage Disclosures →
            </Link>
          )}
        </div>
      )}

      {disclosureReadiness && disclosureReadiness.ready && disclosureReadiness.staleDisclosureIds.length > 0 && (
        <div
          className="flex flex-col gap-2 p-4 bg-primary/10 border border-primary/20 rounded-lg"
          data-testid="disclosure-staleness-info"
          role="status"
        >
          <p className="text-sm font-semibold text-primary m-0">
            Some disclosures may be outdated
          </p>
          <p className="text-sm text-primary/80 m-0">
            Consider reviewing:{' '}
            <strong>
              {disclosureReadiness.staleDisclosureIds
                .map((id) => id.replace(/_/g, ' '))
                .join(', ')}
            </strong>
          </p>
          {unitId && (
            <Link
              to={`/units/${unitId}/disclosures`}
              className="text-sm font-semibold text-primary underline"
              data-testid="nav-unit-disclosures-stale"
            >
              Review Disclosures →
            </Link>
          )}
        </div>
      )}

      <div data-testid="jurisdiction-display">
        <h3 className="text-base font-semibold text-foreground mb-1">Jurisdiction</h3>
        <p className="text-sm text-foreground">{jurisdictionInfo.displayName}</p>
        <div className="flex flex-wrap gap-2 mt-2">
          {jurisdictionInfo.chain.map((item) => (
            <Badge key={item} variant="info" className="rounded-full font-medium">
              {item}
            </Badge>
          ))}
        </div>
      </div>

      <JurisdictionCoverageStatement coverage={jurisdictionInfo.coverage} subject="lease" />

      <div
        data-testid="jurisdiction-rules-summary"
        className="bg-accent/50 rounded-lg border border-border p-5 flex flex-col gap-4"
      >
        <h3 className="text-sm font-semibold text-foreground m-0">Applicable Rules</h3>

        {rules.notice_periods && (
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Notice Periods
            </h4>
            <dl className="flex flex-col gap-1">
              {rules.notice_periods.entry_hours_minimum != null && (
                <div className="flex justify-between text-sm">
                  <dt className="text-muted-foreground">Minimum entry notice</dt>
                  <dd className="font-medium text-foreground">
                    {rules.notice_periods.entry_hours_minimum} hours
                  </dd>
                </div>
              )}
              {rules.notice_periods.termination_month_to_month_landlord_days != null && (
                <div className="flex justify-between text-sm">
                  <dt className="text-muted-foreground">Month-to-month termination (landlord)</dt>
                  <dd className="font-medium text-foreground">
                    {rules.notice_periods.termination_month_to_month_landlord_days} days
                  </dd>
                </div>
              )}
              {rules.notice_periods.termination_month_to_month_tenant_days != null && (
                <div className="flex justify-between text-sm">
                  <dt className="text-muted-foreground">Month-to-month termination (tenant)</dt>
                  <dd className="font-medium text-foreground">
                    {rules.notice_periods.termination_month_to_month_tenant_days} days
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {rules.security_deposit && (
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Security Deposit
            </h4>
            <dl className="flex flex-col gap-1">
              {rules.security_deposit.max_multiplier != null ? (
                <div className="flex justify-between text-sm">
                  <dt className="text-muted-foreground">Maximum deposit</dt>
                  <dd className="font-medium text-foreground">
                    {rules.security_deposit.max_multiplier}x monthly rent
                  </dd>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">No maximum limit</div>
              )}
              {rules.security_deposit.interest_required != null && (
                <div className="flex justify-between text-sm">
                  <dt className="text-muted-foreground">Interest required</dt>
                  <dd className="font-medium text-foreground">
                    {rules.security_deposit.interest_required ? 'Yes' : 'No'}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {rules.late_fees && (
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Late Fees
            </h4>
            <dl className="flex flex-col gap-1">
              {rules.late_fees.grace_period_days != null && (
                <div className="flex justify-between text-sm">
                  <dt className="text-muted-foreground">Grace period</dt>
                  <dd className="font-medium text-foreground">
                    {rules.late_fees.grace_period_days} days
                  </dd>
                </div>
              )}
              {rules.late_fees.max_flat_amount != null && (
                <div className="flex justify-between text-sm">
                  <dt className="text-muted-foreground">Max flat late fee</dt>
                  <dd className="font-medium text-foreground">
                    ${rules.late_fees.max_flat_amount}
                  </dd>
                </div>
              )}
              {rules.late_fees.max_percentage != null && (
                <div className="flex justify-between text-sm">
                  <dt className="text-muted-foreground">Max late fee %</dt>
                  <dd className="font-medium text-foreground">
                    {rules.late_fees.max_percentage}% of rent
                  </dd>
                </div>
              )}
              {rules.late_fees.allowed_types && (
                <div className="flex justify-between text-sm">
                  <dt className="text-muted-foreground">Allowed fee types</dt>
                  <dd className="font-medium text-foreground">
                    {rules.late_fees.allowed_types.join(', ')}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}

        {rules.rent_control && (
          <div>
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
              Rent Control
            </h4>
            <dl className="flex flex-col gap-1">
              <div className="flex justify-between text-sm">
                <dt className="text-muted-foreground">Applies</dt>
                <dd className="font-medium text-foreground">
                  {rules.rent_control.applicable ? 'Yes' : 'No'}
                </dd>
              </div>
              {rules.rent_control.applicable && rules.rent_control.max_annual_increase_formula && (
                <div className="flex justify-between gap-4 text-sm">
                  <dt className="text-muted-foreground">Annual increase cap</dt>
                  <dd className="text-right font-medium text-foreground">
                    {rules.rent_control.max_annual_increase_formula}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        )}
      </div>

      <p className="text-sm text-muted-foreground">
        Review the jurisdiction rules above. These constraints will be applied throughout the lease creation process.
        Click <strong>Next</strong> to continue.
      </p>
    </div>
  );
}
